import React, { useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';

import { Icon } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { SectionHeader } from '~/components';
import { PaginatedDataTable, type PaginatedDataTableRef, useTimeFormat } from '~/components/Table';
import { useNavigate } from '~/lib/navigation';

import { disbursementFilter, getDisbursementColumns, handleFetchDisbursements } from './Disbursement/Overview';
import { LoanProps } from './Loan';

const LoanDisbursementsTable: React.FC<{ loanId: string }> = ({ loanId }) => {
  const navigate = useNavigate();
  const { timeFormat, onTimeFormatChange } = useTimeFormat('loanDisbursements');
  const tableRef = useRef<PaginatedDataTableRef>(null);
  const loanIdRef = useRef(loanId);

  useEffect(() => {
    if (loanIdRef.current === loanId) {
      return;
    }
    loanIdRef.current = loanId;
    tableRef.current?.refetch();
  }, [loanId]);

  return (
    <PaginatedDataTable<Record<string, any>>
      ref={tableRef}
      tableId="loanDisbursements"
      fetch={(params) => handleFetchDisbursements({ ...params, loanId }, navigate)}
      filter={disbursementFilter}
      empty={{
        icon: <Icon.AnimationHandCoins />,
        title: 'No disbursements found',
        description: 'No disbursements were found for this loan.',
      }}
      columns={getDisbursementColumns(timeFormat, onTimeFormatChange)}
      onRowClick={(row) => {
        navigate(`${ROUTE.LOANS}/disbursements/edit/${row.id}`);
      }}
    />
  );
};

export const PageLoanDisbursements: React.FC = () => {
  const { loan } = useOutletContext<LoanProps>();

  if (!loan?.id) {
    return null;
  }

  return (
    <>
      <SectionHeader text="Disbursements" />
      <LoanDisbursementsTable loanId={loan.id} />
    </>
  );
};
